/**
 * Runs — the landing screen, because the first question every morning is
 * "did last night's dispatch go out?"
 *
 * Design decisions worth keeping:
 *
 *  - `stale` is its own column value with its own colour and glyph, never merged into
 *    `failed`. A stale run means the source object was missing or older than its window
 *    and the dispatcher correctly sent nothing (CONTRACT §8.3). Treating that as a
 *    failure trains operators to ignore real failures.
 *  - `partial` is NOT a status either: a partial run was sent, and it covers less than
 *    its dates claim (CONTRACT-COVERAGE). The operator scanning this list is asking "can
 *    I send this to a client?", so a partial run is marked in FORM as well as colour —
 *    beaded badge, segmented row rule — and its Window cell shows the coverage the file
 *    actually has in place of the window that was requested.
 *  - The list polls itself while any visible run is still moving, but a poll never
 *    flips the table back to a loading skeleton — the rows update in place. A table
 *    that shimmers every eight seconds is unreadable.
 *  - Filters live in the query string, so "this is the one that broke" can be pasted
 *    into a ticket. Every filter change resets the cursor: a cursor belongs to the
 *    query that produced it.
 *
 * The row, and why it is shaped this way:
 *
 *  - ONE LINE PER CELL. The client cell used to be a name over its own slug, six
 *    rendered lines in a 103px column, and it set the height of most rows in the
 *    console: the measured average row was 87px against a stated 28px, and seven runs
 *    filled a 1440x900 screen. The slug, the relative time and the requested window are
 *    all recoverable — on a `title`, or on the run's own page — and none of them is what
 *    the row is read for.
 *  - THE NOTE IS THE ONLY UNCONSTRAINED COLUMN. `.table` is `table-layout: fixed`, so
 *    the declared widths bind and the note absorbs every remaining pixel. It wraps in
 *    full: an error is never cut to 70 characters with the remainder in a tooltip,
 *    because a tooltip does not survive a screenshot pasted into a ticket, cannot be
 *    selected, is unreachable by keyboard and does not exist on touch — and every
 *    workflow on this console ends in a ticket.
 *  - THE WIDTH BUDGET, AND WHY SHAPE IS GONE. Status 140 + Client 180 + Window 212 +
 *    three counts at 96 = 820, leaving 196px for the note inside the 1,016px a 1280px
 *    laptop actually has; Started is the one rank-4 column nominated `optional`, so it is
 *    what disappears below 1280 — never the state, the subject or the evidence. Report
 *    shape was a ninth column, and with it the note measured 95px at 1440: a stale run's
 *    reason rendered as a ten-line ribbon one word wide, in a 110px row. A table that
 *    does not fit the budget has too many columns, not a width problem, and shape is the
 *    one fact here that cannot be wrong and never changes on its own. It is stated in
 *    words on the run's own page, one click away, and in the Downloads list.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Badge, { CoverageBadge, StatusBadge, MisalignedBadge } from '@/components/Badge';
import Button from '@/components/Button';
import EmptyState from '@/components/EmptyState';
import { PageHeader } from '@/components/Layout';
import Pagination, { useCursorPagination } from '@/components/Pagination';
import { Checkbox, Select } from '@/components/Field';
import Table, { type Column } from '@/components/Table';
import { api, errorMessage } from '@/lib/api';
import {
  coverageOf,
  describeWindow,
  formatCount,
  isStalledRun,
  istDateRange,
  istDateTime,
  istTime,
  pluralise,
  runWindowLabel,
  timeAgo,
} from '@/lib/format';
import type { Coverage } from '@/lib/format';
import { RUN_IN_FLIGHT, RUN_STATUSES } from '@/lib/types';
import type { Client, Page, Run, RunStatus } from '@/lib/types';

const POLL_MS = 8000;

function isRunStatus(value: string): value is RunStatus {
  return (RUN_STATUSES as readonly string[]).includes(value);
}

/**
 * The coverage qualifier as a LIST row states it: the bare state word, no clock.
 *
 * `CoverageBadge` appends the covered-to time when it has one, which is right on a detail
 * page and wrong in a row: the label measured 217px inside a column declared 124px, so
 * the column ignored its width and took the difference from the Note — and the clock was
 * already in the same row's Window cell, which substitutes the true coverage for the
 * requested window by design.
 *
 * The badge itself is unchanged, so the word, the ◧ glyph, the beaded border and
 * `coverage.summary` on the title all still come from the one place that defines them.
 * The summary keeps the exact time, in a sentence, for the tooltip and the detail page.
 */
function listCoverage(coverage: Coverage): Coverage {
  return { ...coverage, coveredTo: null, coveredToLabel: '' };
}

export default function Runs() {
  const navigate = useNavigate();
  const pager = useCursorPagination();
  const [params, setParams] = useSearchParams();

  const clientId = params.get('client_id') ?? '';
  const statusParam = params.get('status') ?? '';
  const status: RunStatus | '' = isRunStatus(statusParam) ? statusParam : '';
  // Excluded by the API unless asked for (CONTRACT-RECIPIENTS §5). Every dashboard tile
  // counts runs through the same call, so a test must not appear in "runs today" either.
  const includeTest = params.get('include_test') === 'true';
  const filtered = Boolean(clientId || status || includeTest);

  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState<Page<Run>>({ items: [], cursor: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Which query the currently-rendered rows belong to. A query we have never shown
  // gets the skeleton; a repeat of the same query (a poll, a manual refresh) does not.
  const shownQuery = useRef<string | null>(null);
  const queryKey = `${clientId}|${status}|${includeTest}|${pager.cursor ?? ''}|${pager.pageSize}`;

  useEffect(() => {
    let cancelled = false;
    const isNewQuery = shownQuery.current !== queryKey;
    if (isNewQuery) setLoading(true);
    else setRefreshing(true);

    api.runs
      .list({
        cursor: pager.cursor,
        limit: pager.pageSize,
        client_id: clientId || undefined,
        status: status || undefined,
        include_test: includeTest || undefined,
      })
      .then((result) => {
        if (cancelled) return;
        setPage(result);
        setError(null);
        setRefreshedAt(new Date());
        shownQuery.current = queryKey;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(errorMessage(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // queryKey encodes every filter and the cursor; reloadKey forces a re-fetch.
  }, [queryKey, reloadKey, clientId, status, includeTest, pager.cursor, pager.pageSize]);

  useEffect(() => {
    let cancelled = false;
    api.clients
      .listAll()
      .then((all) => {
        if (!cancelled) setClients(all);
      })
      .catch(() => {
        // A failed client list costs the filter its labels, not the screen its rows.
        if (!cancelled) setClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientOf = useCallback(
    (id: string) => clients.find((entry) => entry.client_id === id) ?? null,
    [clients],
  );

  /**
   * One coverage verdict per row, derived once.
   *
   * `coverageOf` used to be called four times per row per render — status cell, window
   * cell, note cell and `rowTone` — on a list that re-renders every eight seconds. Four
   * independent call sites is four places a future change can make the badge, the
   * window, the note and the row rule disagree about the same run, which is the exact
   * failure `coverageOf` exists to prevent.
   */
  const coverageByRun = useMemo(() => {
    const map = new Map<string, Coverage>();
    for (const run of page.items) map.set(run.run_id, coverageOf(run));
    return map;
  }, [page.items]);

  const coverageFor = useCallback(
    (run: Run): Coverage => coverageByRun.get(run.run_id) ?? coverageOf(run),
    [coverageByRun],
  );

  /**
   * What is on this page, counted. Every input is already on the row, so this costs no
   * call: `50 runs on this page · 2 failed · 1 stale · 3 part day · 4 in flight`.
   */
  const counts = useMemo(() => {
    let failed = 0;
    let stale = 0;
    let partial = 0;
    let inFlight = 0;
    let stalled = 0;
    for (const run of page.items) {
      if (run.status === 'failed') failed += 1;
      if (run.status === 'stale') stale += 1;
      if (coverageByRun.get(run.run_id)?.isPartial) partial += 1;
      if ((RUN_IN_FLIGHT as readonly string[]).includes(run.status)) {
        if (isStalledRun(run.status, run.started_at)) stalled += 1;
        else inFlight += 1;
      }
    }
    return { failed, stale, partial, inFlight, stalled };
  }, [page.items, coverageByRun]);

  /**
   * Poll only while something is genuinely still moving. A run that has said
   * "Generating" for 21 hours is not in flight, and letting it hold the poll open meant
   * the page never settled — it re-rendered every eight seconds, for ever, over a run
   * nobody was going to see finish.
   */
  useEffect(() => {
    if (counts.inFlight === 0) return;
    const timer = window.setInterval(() => setReloadKey((key) => key + 1), POLL_MS);
    return () => window.clearInterval(timer);
  }, [counts.inFlight]);

  const setFilter = (key: 'client_id' | 'status' | 'include_test', value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    pager.reset();
  };

  const clearFilters = (): void => {
    setParams(new URLSearchParams(), { replace: true });
    pager.reset();
  };

  const columns: Column<Run>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      render: (run) => {
        const coverage = coverageFor(run);
        return (
          <div className="stack-tight" style={{ alignItems: 'flex-start' }}>
            {/* `startedAt`: past the stall threshold this renders static, warn and ◷ with
                the measured elapsed beside it, instead of a pulsing dot on a run that
                died overnight. The status word itself is never rewritten. */}
            <StatusBadge status={run.status} startedAt={run.started_at} />
            {/* Coverage sits under the status because it qualifies it: "sent" and "sent a
                part-day" are different answers to the only question this list is read
                for. */}
            <CoverageBadge coverage={listCoverage(coverage)} />
            {/* A test send is a classification, not a third state pill. */}
            {run.is_test ? (
              <Badge
                variant="plain"
                glyph="⚗"
                title="A test send to an internal address — left out of this client's figures"
              >
                Test
              </Badge>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'client',
      header: 'Client',
      width: '180px',
      render: (run) => {
        const client = clientOf(run.client_id);
        const title = client
          ? [client.name, client.company_name, client.client_id]
              .filter((part, index, all) => part && all.indexOf(part) === index)
              .join(' · ')
          : run.client_id;
        return (
          <span className="truncate" title={title}>
            {client?.name ?? run.client_id}
          </span>
        );
      },
    },
    {
      key: 'started',
      header: 'Started (IST)',
      width: '165px',
      nowrap: true,
      // Rank 4: the run's own page carries the exact time, and the subtitle above carries
      // the list's freshness. It is one of the two columns that go below 1280px.
      optional: true,
      render: (run) => (
        // Absolute IST stays on screen (CONTRACT §8.7); the relative time is the
        // supplement and moves to the title, where it costs no width on 200 rows.
        <span className="num" title={timeAgo(run.started_at)}>
          {istDateTime(run.started_at)}
        </span>
      ),
    },
    {
      key: 'window',
      header: 'Window (IST)',
      // 212, not the 190 the plan estimated: `01 Sep 2026 → 16 Sep 2026 IST` measures
      // ~180px at the table's 12px, and at 190 it wrapped on every multi-day window —
      // which is most rows, and would have cost the density this work is for.
      width: '212px',
      headerTitle:
        'For a partial run this is the coverage the source actually had, not the window that was asked for.',
      render: (run) => {
        const requested =
          run.window_from && run.window_to
            ? istDateRange(run.window_from, run.window_to)
            : (run.window_label ?? (run.window ? describeWindow(run.window) : '—'));
        const spec = run.window ? describeWindow(run.window) : null;
        return (
          // One grammar: the resolved IST range, always — derived in lib/format so this
          // screen and the dashboard cannot state different things about the same run.
          <span
            className="num"
            title={spec ? `Requested: ${spec} · ${requested}` : `Requested: ${requested}`}
          >
            {runWindowLabel(run)}
          </span>
        );
      },
    },
    {
      key: 'rows_in',
      header: 'Rows read',
      align: 'right',
      mono: true,
      width: '96px',
      render: (run) => formatCount(run.rows_in),
    },
    {
      key: 'rows_out',
      header: 'Rows sent',
      align: 'right',
      mono: true,
      width: '96px',
      render: (run) => formatCount(run.rows_out),
    },
    {
      key: 'recipients',
      // "To" sat between two right-aligned number columns and read as a third row
      // metric. A header that needs a tooltip to be read at all is a broken header.
      header: 'Recipients',
      align: 'right',
      mono: true,
      width: '96px',
      headerTitle: 'Recipients resolved for this run',
      render: (run) => formatCount(run.recipients.length),
    },
    {
      key: 'note',
      header: 'Note',
      // The evidence column: no width, so fixed layout hands it every remaining pixel,
      // and it wraps rather than being cut.
      wrap: true,
      render: (run) => {
        const coverage = coverageFor(run);
        const marks: ReactNode[] = [];

        // A list, not a first-match ladder. A failed run that also lost recipients from
        // its schedule used to show only the error — and `recipients_unknown` exists
        // (per its own docstring) to make a schedule quietly mailing fewer people than
        // it lists impossible, which is likeliest exactly when something else is wrong.
        // Severity order: the error first, and widest.
        if (run.error) {
          marks.push(
            <div key="error" className="small" style={{ color: 'var(--danger)' }}>
              {run.error}
            </div>,
          );
        }
        if (coverage.rowsMisaligned !== null && coverage.rowsMisaligned > 0) {
          marks.push(<MisalignedBadge key="misaligned" coverage={coverage} />);
        }
        if (run.recipients_unknown && run.recipients_unknown.length > 0) {
          marks.push(
            <div key="unknown" className="stack-tight">
              <span className="small" style={{ color: 'var(--danger)' }}>
                ⊘ {pluralise(run.recipients_unknown.length, 'recipient')} on the schedule no
                longer exist — they were not mailed
              </span>
              {/* Which ones, as selectable text. A count alone is not actionable and the
                  answer was in a tooltip that cannot be copied. */}
              {run.recipients_unknown.map((entry) => (
                <span key={entry} className="mono xs">
                  {entry}
                </span>
              ))}
            </div>,
          );
        }
        if (run.unranked_dispositions.length > 0) {
          marks.push(
            <Badge
              key="unranked"
              tone="muted"
              glyph="?"
              // Disposition values exactly as stored — no case-correcting, no expanding,
              // no spell-fixing (CONTRACT §5). The run's own page lists them in full.
              title={run.unranked_dispositions.map((value) => value || '(blank)').join(', ')}
            >
              {pluralise(run.unranked_dispositions.length, 'unranked disposition')}
            </Badge>,
          );
        }

        if (marks.length === 0) return <span className="dim">—</span>;
        return (
          <div className="stack-tight" style={{ alignItems: 'flex-start' }}>
            {marks}
          </div>
        );
      },
    },
  ];

  return (
    <div className="stack">
      <PageHeader
        title="Runs"
        subtitle={
          <>
            Every dispatch, newest first.{' '}
            {refreshedAt ? (
              <span className="dim">
                Updated {istTime(refreshedAt)}
                {counts.inFlight > 0 ? ' · auto-refreshing while runs are in flight' : ''}
              </span>
            ) : null}
          </>
        }
        actions={
          <Button
            onClick={() => setReloadKey((key) => key + 1)}
            loading={refreshing}
            icon={<span aria-hidden="true">↻</span>}
          >
            Refresh
          </Button>
        }
      />

      <div className="toolbar">
        <label className="cluster-tight">
          <span className="small dim">Client</span>
          <Select
            size="sm"
            value={clientId}
            aria-label="Filter by client"
            onChange={(event) => setFilter('client_id', event.target.value)}
          >
            <option value="">All clients</option>
            {clients.map((client) => (
              <option key={client.client_id} value={client.client_id}>
                {client.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="cluster-tight">
          <span className="small dim">Status</span>
          <Select
            size="sm"
            value={status}
            aria-label="Filter by status"
            onChange={(event) => setFilter('status', event.target.value)}
          >
            <option value="">Any status</option>
            {RUN_STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </label>

        <Checkbox
          checked={includeTest}
          onChange={(event) => setFilter('include_test', event.target.checked ? 'true' : '')}
          label={<span className="small dim">Include test sends</span>}
        />

        {filtered ? (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}

        <span className="grow" />

        {/*
          What is on this page, without a scan. The two that map to a status narrow the
          list through the same query-string filter the dropdown uses, so the cursor is
          reset with them. Part day, not moving and in flight are STATED, not clickable:
          `partial` is deliberately not a status, and a client-side filter over held rows
          would disagree with the server filter and the cursor.
        */}
        {!loading && page.items.length > 0 ? (
          <span className="cluster-tight small dim">
            <span>{pluralise(page.items.length, 'run')} on this page</span>
            {counts.failed > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <Button size="sm" variant="ghost" onClick={() => setFilter('status', 'failed')}>
                  {counts.failed} failed
                </Button>
              </>
            ) : null}
            {counts.stale > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <Button size="sm" variant="ghost" onClick={() => setFilter('status', 'stale')}>
                  {counts.stale} stale
                </Button>
              </>
            ) : null}
            {counts.partial > 0 ? <span>· {counts.partial} part day</span> : null}
            {counts.stalled > 0 ? (
              <span style={{ color: 'var(--warn)' }}>· {counts.stalled} not moving</span>
            ) : null}
            {counts.inFlight > 0 ? <span>· {counts.inFlight} in flight</span> : null}
          </span>
        ) : null}

        <Button size="sm" variant="ghost" onClick={() => navigate('/schedules')}>
          Schedules →
        </Button>
      </div>

      {/*
        Above the table, not below it. With 50 rows loaded this sat roughly 4,000px below
        the fold, so an operator read stale rows as current — which on this screen means
        believing a dispatch went out.
      */}
      {error && page.items.length > 0 ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>{error} Showing the last rows that loaded — they may be out of date.</span>
        </div>
      ) : null}

      <Table
        columns={columns}
        rows={page.items}
        rowKey={(run) => run.run_id}
        loading={loading}
        dense
        stickyHeader
        onRowClick={(run) => navigate(`/runs/${run.run_id}`)}
        rowTone={(run) =>
          run.status === 'failed'
            ? 'danger'
            : run.status === 'stale'
              ? 'warn'
              : // A run that cannot still be running is a problem, and the row rule is
                // what survives a greyscale screenshot.
                isStalledRun(run.status, run.started_at)
                ? 'warn'
                : coverageFor(run).isPartial
                  ? 'partial'
                  : 'default'
        }
        empty={
          error ? (
            <EmptyState
              tone="danger"
              title="Could not load runs"
              description={error}
              action={
                <Button variant="primary" onClick={() => setReloadKey((key) => key + 1)}>
                  Retry
                </Button>
              }
            />
          ) : filtered ? (
            <EmptyState
              tone="filtered"
              title="No runs match those filters"
              description="Nothing has been dispatched for that client and status."
              action={
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No runs yet"
              description="Nothing has been dispatched. Create a schedule, dry-run it, and send."
              action={
                <Button variant="primary" onClick={() => navigate('/schedules')}>
                  Go to schedules
                </Button>
              }
            />
          )
        }
        footer={
          <Pagination
            page={pager.page}
            pageSize={pager.pageSize}
            count={page.items.length}
            hasNext={Boolean(page.cursor)}
            hasPrev={pager.hasPrev}
            onNext={() => pager.next(page.cursor)}
            onPrev={pager.prev}
            onPageSize={pager.setPageSize}
            loading={loading || refreshing}
            label="runs"
          />
        }
      />
    </div>
  );
}
